import React, { useEffect, useRef, useState } from "react";
import { SimulationType } from "./types";

export interface SimulationTypeOption {
  type: SimulationType;
  calc: string;
  label: string;
  group: string;
}

const OPTIONS: SimulationTypeOption[] = [
  {
    type: "ML",
    calc: "ML",
    label: "CATIVO X MERCADO LIVRE",
    group: "Comparação de mercado",
  },
  {
    type: "APE",
    calc: "APE",
    label: "CATIVO X AUTOPRODUTOR",
    group: "Comparação de mercado",
  },
  {
    type: "MLXAPE",
    calc: "APE",
    label: "MERCADO LIVRE X AUTOPRODUTOR",
    group: "Comparação de mercado",
  },
  {
    type: "GDXAPE",
    calc: "GDXAPE",
    label: "GD X AUTOPRODUTOR",
    group: "Comparação de mercado",
  },
  {
    type: "MLBESS",
    calc: "ML",
    label: "MERCADO LIVRE COM E SEM BATERIA",
    group: "Impacto da bateria",
  },
  {
    type: "APEBESS",
    calc: "APE",
    label: "AUTOPRODUTOR COM E SEM BATERIA",
    group: "Impacto da bateria",
  },
];

interface SimulationTypeSelectorProps {
  selected: string;
  fortlev?: boolean;
  onSelect: (option: SimulationTypeOption) => void;
}

export const SimulationTypeSelector = ({
  selected,
  onSelect,
}: SimulationTypeSelectorProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption =
    OPTIONS.find((option) => option.type === selected) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [open]);

  const choose = (option: SimulationTypeOption) => {
    onSelect(option);
    setOpen(false);
  };

  let lastGroup = "";

  return (
    <div className="sim-select" ref={containerRef}>
      <button
        type="button"
        className={`sim-select-trigger${
          open ? " sim-select-trigger-open" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="sim-select-trigger-title">{selectedOption.label}</span>
        <svg
          className="sim-select-chevron"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="sim-select-panel" role="listbox">
          {OPTIONS.map((option) => {
            const showGroup = option.group !== lastGroup;
            lastGroup = option.group;

            return (
              <React.Fragment key={option.type}>
                {showGroup && (
                  <div className="sim-select-group">{option.group}</div>
                )}
                <div
                  className="sim-select-option"
                  role="option"
                  aria-selected={option.type === selected}
                  tabIndex={0}
                  onClick={() => choose(option)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      choose(option);
                    }
                  }}
                >
                  <span className="sim-select-option-title">
                    {option.label}
                  </span>
                  <svg
                    className="sim-select-check"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
};
